/* eslint-disable react/react-in-jsx-scope */
import { useState , useEffect , useRef, lazy } from "react";
import ReactBreadcrumb from "@/components/breadcrumb/Breadcrumb";
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RefreshCw, Send , Loader , FileBadge, ChevronsRight, Trash, Lock, Check, Edit } from "lucide-react";
import { useTranslation } from "react-i18next";
import usePostData from "@/hooks/usePostData";
import FinancerInput from "@/components/fields/FinancerInput";
import ProjectInput from "@/components/fields/ProjectInput";
import { useAuth } from "@/auth/hooks/useAuth";
import { toast } from '@/components/ui/use-toast';
import n2words from 'n2words';

const PdfUploader = lazy(
  () => import("@/components/custom/CerfaFileUploader")
);

export default function AddCerfa() {
  const { user} = useAuth();

  const { t } = useTranslation("dashboard", {
    keyPrefix: "certificats",
  });
  const { postData } = usePostData();
  const [financer, setFinancer] = useState(null);
  const [createdProject, setCreatedProject] = useState(null);
  const [montant, setMontant] = useState<number|string>("");
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [disableProject, setDisableProject] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState(null);


  const [isDisabled, setIsDisabled] = useState(true);
  useEffect(() => {
    setIsDisabled(
      financer === null ||
      createdProject === null ||
      montant === "" ||
      dateDebut === "" ||
      !user || !file
    );
  }, [financer, createdProject, montant, dateDebut, user,file]);

  // États partagés pour tout le formulaire
  const [form, setForm] = useState({
    montantNature: "",    
    montantNatureLettre: "",
    montantVersement: "", 
    montantVersementLettre: "", 
    montantTotal: "",    
    montantTotalLettre: "", 

    // Page 4 - Dates
    dateDebut: "",        
    dateFin: "",         

    // for database 
    financerId: "",
    projectId: "",

    file: null,

  });

  const update = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    if(createdProject) {
        update("objet", "Financement du projet "+createdProject.name);
        update("projectId", createdProject.id);
        setDisableProject(false);
    } else {
      update("objet", "");
      update("projectId", "");
      setDisableProject(true);
    }
    
  }, [createdProject]);



  useEffect(() => {
    if(financer) {
      update("financerId", financer.id);
    } else {
      update("financerId", "");
    }
  }, [financer]);

  useEffect(() => {
      update("dateDebut", dateDebut);
  }, [dateDebut]);
  useEffect(() => {
      update("dateFin", dateFin);
  }, [dateFin]);
  useEffect(() => {
      update("montantTotal", montant);
      if(montant != "") {
        const enLettres = n2words(montant, { lang: 'fr' });
        update("montantTotalLettre", enLettres+" Euros");
      }
      
  }, [montant]);

  useEffect(() => {
    if(financer) {
      setDisableProject(false);
    } else {
      setDisableProject(true);
    }
    
  }, [financer]);

  useEffect(() => {
    update("file", file);
  }, [file]);

  const submitFormulaire = async () => {
    setIsLoading(true);
    const urlApi = `${import.meta.env.VITE_IP_API}/api/add-new-certificate`;

    try {
      // Créer un FormData pour envoyer le fichier et les champs texte
      const formData = new FormData();
      // Ajouter les champs texte
      formData.append("projectId", form.projectId);
      formData.append("financerId", form.financerId);
      formData.append("dateDebut", form.dateDebut);
      formData.append("dateFin", form.dateFin);
      formData.append("montantTotal", form.montantTotal);

      // Ajouter le fichier PDF
      if (form.file) {
        formData.append("file", form.file);
      } else {
        toast({
          title: "Erreur",
          description: "Veuillez sélectionner un fichier PDF",
          className: "text-red-500",
        });
        setIsLoading(false);
        return;
      }

      // On ne met PAS le Content-Type à 'application/json' pour FormData
      const token =
        localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken");

      const res = await fetch(urlApi, {
        method: "POST",
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : {},
        body: formData,
      });

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result.message || "Une erreur s’est produite");
      }

      toast({
        title: "Success",
        description: "Certificat généré avec succès",
        duration: 4000,
      });
      window.location.href = "/dashboard/certificats";
    } catch (err: any) {
      toast({
        title: "Erreur",
        description: err.message || "Une erreur inconnue s’est produite",
        className: "text-red-500",
      });
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div>

      <ReactBreadcrumb
        items={[
          {
            href: ``,
            name: "Mes reçus fiscaux",
          },
        ]}
        isTruncated={false} />

      <div className="flex flex-wrap gap-5 items-center justify-between mt-5">
        <h1
          className="mb-2 mt-4 text-[24px] font-medium inline-block"
          id="program-title"
        >
          {t("Formulaire")}
        </h1>
      </div> 
    
      <div className="w-full max-w-5xl p-6 border rounded-lg shadow-md space-y-6 mt-3">
        <div id="addFinancementContent">
          <h2 className="text-xl font-semibold mb-4">{t("Informations sur le financement")}</h2>
          <div className=" w-full gap-4">
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Montant")}
                </label>
                <Input
                  type="number"
                  placeholder="Montant"
                  value={montant}
                  onChange={(e) => setMontant(parseFloat(e.target.value))}
                  required
                  step="0.01"
                  className={""}
                />
              </div>
          </div>
          <br/>
          <div className="block mb-2 flex w-full gap-4 mt-2">
            <div className="w-1/2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("Financeur")}
              </label>

              <FinancerInput
                className="mt-1"
                selected={financer}
                handleChange={(value) => {
                    setFinancer(value);
                }}
                placeholder={t("Saisissez un financeur...")}
              />
              
            </div>
            <div className="w-1/2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("Projet")}
              </label>
                <ProjectInput
                  className="mt-1"
                  selected={financer}
                  handleChange={(value) => {
                    setCreatedProject(value);
                  }}
                  idFinancer={financer ? financer.id : null}
                  disableProject={disableProject}
                />
              </div>
          </div>
          <br/> 
          <h3 className="font-bold text-gray-700 tracking-wide border-b-2 border-[#1d5214] pb-1 mt-6 mb-4">
            {t("Date ou période au cours de laquelle")} <span className="text-red-500">*</span>
          </h3>
              
          <div className="flex w-full gap-4 mt-4">
              {/* Date de début */}
              <div className="w-1/2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Du ou le")}
                  </label>
                  <input
                    type="date"
                    value={dateDebut}
                    max={dateFin || undefined}
                    onChange={(e) => setDateDebut(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                  />
              </div>
  
              {/* Date de fin */}
              <div className="w-1/2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("au")}
                  </label>
                  <input
                    type="date"
                    value={dateFin}
                    min={dateDebut || undefined}
                    onChange={(e) => setDateFin(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                  />
              </div>
          </div>

          {/* Feedback utilisateur (facultatif mais sympa) */}
          {dateDebut && (
          <div className="mt-2 text-sm text-gray-600">
              {dateFin ? (
              <>{t("Versement du")} <strong>{dateDebut}</strong> {t("au")} <strong>{dateFin}</strong></>
              ) : (
              <>{t("Versement à la date du")} <strong>{dateDebut}</strong></>
              )}
          </div>
          )}   

          <p className="mt-4 text-sm text-gray-600">
            <span className="text-red-500">*</span>
            <i className="text-xs">{t("L’organisme bénéficiaire peut établir")}
            </i>
          </p> 
        </div>
        <PdfUploader onFileChange={setFile} />

        <div className="flex justify-between pt-6">
          <Button
              onClick={() => {
                submitFormulaire();
              }}
              disabled={isLoading || isDisabled}
            >
              {isLoading ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  {t("Envoi en cours")}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Enregistrer le reçu
                </>
              )}
          </Button>
          
        </div>
      </div>
    </div>
  );
}
